<template>
  <BlogPostLayout>
    <template #image>
      <BlogHeader
        :title="postData.title"
        :excerpt="postData.excerpt"
        :image="postData.image"
        :author="postData.author"
        :date="postData.date"
        :updatedAt="postData.updatedAt"
        :categories="postData.categories"
        :readTime="postData.readTime"
      />
    </template>
    <template #content>
      <article class="blog-content" lang="es">
        <section class="intro">
          <p>
            «Tour virtual universidad» figura entre las consultas de mayor
            crecimiento en Google Trends — una señal clara de que los centros
            educativos han comprendido su importancia. Los establecimientos que
            publican tours virtuales registran
            <strong
              >entre un 30 y un 50 % más de inscripciones a jornadas de puertas
              abiertas</strong
            >
            de estudiantes de otras regiones e internacionales. Aquí tienes la
            guía completa.
          </p>
        </section>

        <section>
          <h2>Por qué los centros educativos necesitan tours virtuales</h2>
          <p>
            Los futuros estudiantes investigan primero en línea antes de visitar
            en persona — o en lugar de hacerlo. Un tour virtual convincente del
            campus puede inclinar la balanza para un candidato remoto que duda
            entre dos centros de igual reputación. La geografía deja de ser un
            obstáculo para atraer a los mejores perfiles.
          </p>
        </section>

        <section>
          <h2>Qué capturar en un tour virtual escolar o universitario</h2>
          <p>
            Para un tour completo, incluye: entrada principal y recepción, aulas
            y anfiteatros, laboratorios e instalaciones especializadas, zonas
            comunes y cafetería, biblioteca, instalaciones deportivas,
            residencias de estudiantes, zonas exteriores y jardines, y el barrio
            circundante.
          </p>
        </section>

        <section>
          <h2>Crear un tour virtual con Travvir</h2>

          <h3>Planificar el recorrido</h3>
          <p>
            Traza el flujo natural de un futuro estudiante: entrada → recepción
            → espacios académicos → espacios sociales → alojamientos. Capturar
            en este orden hace la navegación intuitiva.
          </p>

          <h3>Capturar cada espacio</h3>
          <p>
            Usa la aplicación Travvir para fotografiar cada zona en 360°. Para
            espacios grandes como anfiteatros, captura desde varias posiciones
            para transmitir la escala. Para espacios más pequeños, una captura
            central es suficiente.
          </p>

          <h3>Añadir hotspots informativos</h3>
          <p>
            Travvir te permite añadir hotspots de texto con la información de
            cada sala — aforo, equipamiento, horarios, enlaces a páginas de
            inscripción. Estas capas de información convierten un tour visual en
            un prospecto interactivo.
          </p>

          <h3>Insertar en tu página de admisiones</h3>
          <p>
            Inserta el tour directamente en tu página «Conócenos» o admisiones.
            Añade una llamada a la acción visible («Inscribirse a puertas
            abiertas» o «Solicitar plaza») para convertir el alto engagement
            generado.
          </p>
        </section>

        <section>
          <h2>Casos de uso adicionales</h2>
          <ul>
            <li>
              <strong>Estudiantes con movilidad reducida</strong> — Permite a
              quienes no pueden desplazarse descubrir el campus.
            </li>
            <li>
              <strong>Orientación de nuevos estudiantes</strong> — Ofrece a los
              admitidos un tour de orientación virtual antes de su llegada.
            </li>
            <li>
              <strong>Eventos virtuales</strong> — Combina el tour con una
              sesión Zoom para las jornadas de puertas abiertas en línea.
            </li>
          </ul>
        </section>
        <section class="continue-reading">
          <h2>Seguir Leyendo</h2>
          <ul>
          <li><a href="/blog/es/recorrido-virtual-museo-galeria">Recorrido Virtual para Museos y Galerías: Guía Completa 2026</a></li>
          <li><a href="/blog/es/tour-virtual-negocio-local">Tour Virtual para Negocios Locales: Por Qué lo Necesitas en 2026</a></li>
          <li><a href="/blog/es/crear-tour-virtual-con-movil">Crear un Tour Virtual con el Móvil: Guía Completa 2026</a></li>
          <li><a href="/blog/es/tour-virtual-google-mi-negocio">Tour Virtual en Google Business: Cómo Añadir Fotos 360° a tu Ficha</a></li>
          <li><a href="/blog/es/tour-virtual-360-guia-completa">Tour Virtual 360°: Guía Completa para Principiantes (2026)</a></li>
          </ul>
        </section>



        <RelatedPosts
          :currentSlug="postData.slug"
          :currentCategories="postData.categories"
        />
      </article>
    </template>
  </BlogPostLayout>
</template>

<script setup>
import blogPostsData from "~/data/blogPosts.json";

const postData = blogPostsData.find(
  (p) => p.slug === "tour-virtual-escuelas-universidades",
);

useBlogSeo({
  title: "Tour Virtual para Escuelas y Universidades: Guía Completa | Travvir",
  description:
    "Cómo crear un tour virtual para tu centro educativo o universitario. Guía de captura, hotspots informativos y estrategia de publicación para aumentar las inscripciones.",
  keywords:
    "tour virtual escuela, tour virtual universidad, campus virtual, tour virtual centro educativo",
  slug: "tour-virtual-escuelas-universidades",
  image: postData?.image,
  postData,
  lang: "es",
  faqItems: [
    {
      question:
        "¿Puede un tour virtual sustituir a una jornada de puertas abiertas?",
      answer:
        "La complementa — los centros que ofrecen ambas opciones registran entre un 30 y un 50 % más de inscripciones a puertas abiertas de estudiantes remotos.",
    },
    {
      question: "¿Qué espacios incluir primero?",
      answer:
        "Anfiteatros, laboratorios especializados, biblioteca y residencias de estudiantes — los espacios que diferencian tu oferta.",
    },
  ],
});

</script>
